import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { useClientConfig } from "@/api/queries";
import { authErrorMessage, useAuth } from "@/auth/AuthProvider";
import { Spinner } from "@/components/Spinner";
import { AuthLayout, safeNext } from "./AuthLayout";

/**
 * The verify-email gate (when the backend's `requireEmailVerification` is on): resend the link,
 * or confirm it was opened (re-reads the Firebase user and refreshes the ID token).
 */
export function VerifyEmailPage() {
  const { ready, user, sendVerification, reload, signOut } = useAuth();
  const { data: config } = useClientConfig();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const next = safeNext(params.get("next"), "/app");
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const verified = !!user?.emailVerified || (config && !config.requireEmailVerification);
  useEffect(() => {
    if (ready && user && verified) navigate(next, { replace: true });
  }, [ready, user, verified, next, navigate]);

  if (!ready) {
    return (
      <AuthLayout title="auth.verify.h1" testId="verify-page">
        <Spinner label="Loading" />
      </AuthLayout>
    );
  }
  if (!user) return <Navigate to="/signin" replace />;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
    } catch (e) {
      setError(authErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="auth.verify.h1"
      sub="auth.verify.sub"
      params={{ email: user.email ?? "your email" }}
      testId="verify-page"
    >
      {note && (
        <p className="wp-note" role="status" data-testid="verify-note">
          {note}
        </p>
      )}
      {error && (
        <p className="wp-form-err" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="wp-btn"
        disabled={busy}
        data-testid="verify-confirmed"
        onClick={() =>
          run(async () => {
            const fresh = await reload();
            if (fresh?.emailVerified) {
              // The backend reads `email_verified` from the token: drop anything fetched without it.
              await queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "config" });
              navigate(next, { replace: true });
            } else {
              setNote(
                "We can't see the confirmation yet. Open the link in the email, then try again.",
              );
            }
          })
        }
      >
        I&rsquo;ve confirmed my email
      </button>
      <button
        type="button"
        className="wp-btn g"
        style={{ width: "100%", marginTop: 10 }}
        disabled={busy}
        data-testid="verify-resend"
        onClick={() =>
          run(async () => {
            await sendVerification();
            setNote(
              "We sent another link. It can take a minute to arrive; check your spam folder.",
            );
          })
        }
      >
        Send the link again
      </button>
      <div className="wp-auth-links">
        <button
          type="button"
          className="wp-link"
          style={{ background: "none", border: 0, padding: 0, font: "inherit", cursor: "pointer" }}
          data-testid="verify-signout"
          onClick={async () => {
            await signOut();
            navigate("/signin", { replace: true });
          }}
        >
          Use a different account
        </button>
      </div>
    </AuthLayout>
  );
}
